import { matchParticipantShortName, type MatchParticipantBrief, type TournamentMatch } from "./api";
import { buildConsolationBracket, buildKnockoutBracket, type KnockoutBracketData } from "./bracketFromMatches";

/**
 * Secciones del Draw del jugador (Eduardo 2026-09-05):
 * - Una sección POR CATEGORÍA (división). Dentro de la misma sección va la
 *   tabla de round robin de sus grupos y, debajo, el cuadro de playoffs —
 *   ya no son dos acordeones distintos (grupos por un lado, brackets por otro).
 * - En ESCALERA la fase de jornadas no es un round robin: la sección Draw
 *   solo muestra el cuadro de playoffs; las tablas de grupo se omiten.
 */

export interface DrawDivisionBrief {
  id: number;
  name: string;
  format_type?: string | null;
}

export interface PlayerDrawGroup {
  key: string;
  name: string;
  participantsById: Map<number, MatchParticipantBrief>;
  matchesByPair: Map<string, TournamentMatch>;
  /** Orden de la hoja del grupo acomodado por el organizador (ids de
   * participante; Eduardo 2026-10-01). Sin él, el orden de siempre. */
  order?: number[];
}

export interface PlayerDrawSection {
  key: string;
  name: string;
  formatType: string | null;
  /** Escalera: sin tablas de grupo, solo el cuadro. */
  isLadder: boolean;
  groups: PlayerDrawGroup[];
  bracket: KnockoutBracketData;
  /** Cuadro de consolación (rondas «C · …»); sin rondas si no hay. */
  consolation: KnockoutBracketData;
  /** El jugador tiene partidos en esta categoría. */
  isMine: boolean;
}

export function divisionKeyFor(match: TournamentMatch): string {
  return match.division_id === null || match.division_id === undefined ? "general" : String(match.division_id);
}

export function playerPairKey(a: number, b: number): string {
  return a < b ? `${a}-${b}` : `${b}-${a}`;
}

function compareByRank(a: MatchParticipantBrief, b: MatchParticipantBrief): number {
  const aRank = a.current_rank ?? a.initial_rank ?? a.tournament_rank ?? Number.POSITIVE_INFINITY;
  const bRank = b.current_rank ?? b.initial_rank ?? b.tournament_rank ?? Number.POSITIVE_INFINITY;
  if (aRank !== bRank) return aRank - bRank;
  return matchParticipantShortName(a).localeCompare(matchParticipantShortName(b), "es");
}

/** Filas de la tabla del grupo. Grupo acomodado (`order`): primero los de la
 * hoja en su lugar y después cualquier otro (p. ej. una baja que sigue en sus
 * partidos) con el orden de siempre. Sin `order`, el orden de siempre. */
export function sortedGroupParticipants(group: PlayerDrawGroup): MatchParticipantBrief[] {
  const all = [...group.participantsById.values()];
  if (!group.order || group.order.length === 0) return all.sort(compareByRank);
  const placed: MatchParticipantBrief[] = [];
  const seen = new Set<number>();
  for (const id of group.order) {
    const brief = group.participantsById.get(id);
    if (brief && !seen.has(id)) {
      seen.add(id);
      placed.push(brief);
    }
  }
  const leftovers = all.filter((brief) => !seen.has(brief.id)).sort(compareByRank);
  return [...placed, ...leftovers];
}

/** Marcador de la celda desde la perspectiva del participante de la FILA. */
export function cellScoreForRow(match: TournamentMatch, rowParticipantId: number): string | null {
  const sets = match.score_sets ?? [];
  if (sets.length > 0) {
    const rowIsP1 = match.player1_participant_id === rowParticipantId;
    return sets
      .map((set) => (rowIsP1 ? `${set.player1_games}-${set.player2_games}` : `${set.player2_games}-${set.player1_games}`))
      .join(", ");
  }
  return match.score_text?.trim() || null;
}

function isCancelledMatch(match: TournamentMatch): boolean {
  return String(match.status) === "cancelled";
}

function matchHasParticipant(match: TournamentMatch, participantId: number | null): boolean {
  if (participantId == null) return false;
  return match.player1_participant_id === participantId || match.player2_participant_id === participantId;
}

export function buildPlayerDrawSections(
  matches: TournamentMatch[],
  options: {
    divisions?: DrawDivisionBrief[] | null;
    tournamentFormatType?: string | null;
    myParticipantId?: number | null;
    /** Orden de la hoja por grupo (`group_orders` del endpoint del jugador). */
    groupOrders?: Record<string, number[]> | null;
  } = {},
): PlayerDrawSection[] {
  const divisions = options.divisions ?? [];
  const tournamentFormat = options.tournamentFormatType ?? null;
  const myParticipantId = options.myParticipantId ?? null;
  const groupOrders = options.groupOrders ?? {};

  const byDivision = new Map<string, { name: string; matches: TournamentMatch[]; groups: Map<number, PlayerDrawGroup>; isMine: boolean }>();
  for (const match of matches) {
    const key = divisionKeyFor(match);
    let scope = byDivision.get(key);
    if (!scope) {
      const brief = divisions.find((division) => String(division.id) === key);
      scope = {
        name: brief?.name?.trim() || match.division_name?.trim() || "General",
        matches: [],
        groups: new Map(),
        isMine: false,
      };
      byDivision.set(key, scope);
    }
    scope.matches.push(match);
    if (matchHasParticipant(match, myParticipantId)) scope.isMine = true;

    if (match.group_id == null) continue;
    const p1 = match.player1;
    const p2 = match.player2;
    if (!p1 || !p2) continue;
    let group = scope.groups.get(match.group_id);
    if (!group) {
      const order = groupOrders[String(match.group_id)];
      group = {
        key: String(match.group_id),
        name: match.group_name?.trim() || `Grupo ${match.group_id}`,
        participantsById: new Map(),
        matchesByPair: new Map(),
        ...(order && order.length > 0 ? { order } : {}),
      };
      scope.groups.set(match.group_id, group);
    }
    const pairKey = playerPairKey(p1.id, p2.id);
    if (group.order && isCancelledMatch(match)) {
      // Solo en un grupo ACOMODADO (con orden de la hoja): un partido
      // cancelado no crea filas (quien solo sale ahí ya no está en el grupo)
      // y nunca tapa al partido vigente de la misma pareja. Sin orden, todo
      // como siempre (abajo).
      if (!group.matchesByPair.has(pairKey)) group.matchesByPair.set(pairKey, match);
      continue;
    }
    for (const brief of [p1, p2]) {
      if (!group.participantsById.has(brief.id)) group.participantsById.set(brief.id, brief);
    }
    // Como siempre: el último partido de la pareja manda (en un grupo
    // acomodado, el vigente sustituye a un cancelado que se vio antes).
    group.matchesByPair.set(pairKey, match);
  }

  const sections: PlayerDrawSection[] = [];
  for (const [key, scope] of byDivision.entries()) {
    const brief = divisions.find((division) => String(division.id) === key);
    const formatType = (brief?.format_type ?? null) || tournamentFormat;
    const isLadder = formatType === "ladder";
    const groups = isLadder
      ? []
      : [...scope.groups.values()]
          // Solo pasa en un grupo acomodado con puros partidos cancelados.
          .filter((group) => group.participantsById.size > 0)
          .sort((a, b) => a.name.localeCompare(b.name, "es", { numeric: true }));
    const bracket = buildKnockoutBracket(scope.matches);
    const consolation = buildConsolationBracket(scope.matches);
    if (groups.length === 0 && bracket.rounds.length === 0) continue;
    sections.push({ key, name: scope.name, formatType, isLadder, groups, bracket, consolation, isMine: scope.isMine });
  }
  return sections.sort((a, b) => a.name.localeCompare(b.name, "es", { numeric: true }));
}
